/**
 * useAgency Hook
 * Custom hook for agency data access and operations
 */

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../store';
import {
  fetchAgencyByIdAsync,
  clearAgency,
  selectAgency,
  selectAgencyLoading,
  selectAgencyError,
  selectAgencyLastFetched,
  selectAgencyInfo,
  selectAgencyAddresses,
  selectAgencyContact,
  selectAgencyLicensing,
  selectAgencyServices,
  selectAgencyOperations,
  selectAgencyInsurance,
  selectAgencyDocuments,
  selectAgencyStatus,
} from '../store/slices/agency.slice';
import type { AgencyProfileResponse } from '../types/agency.types';

/**
 * useAgency Hook Return Type
 */
interface UseAgencyReturn {
  // State
  agency: AgencyProfileResponse | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: string | null;

  // Derived data
  agencyInfo: AgencyProfileResponse['agencyInfo'] | undefined;
  addresses: AgencyProfileResponse['addresses'] | undefined;
  contact: AgencyProfileResponse['contact'] | undefined;
  licensing: AgencyProfileResponse['licensing'] | undefined;
  services: AgencyProfileResponse['services'] | undefined;
  operations: AgencyProfileResponse['operations'] | undefined;
  insurance: AgencyProfileResponse['insurance'] | undefined;
  documents: AgencyProfileResponse['documents'] | undefined;
  status: AgencyProfileResponse['status'] | undefined;

  // Actions
  fetchAgency: (agencyId: string) => Promise<AgencyProfileResponse>;
  refreshAgency: () => Promise<AgencyProfileResponse | null>;
  clearAgencyData: () => void;
}

/**
 * useAgency Hook
 * Provides agency data and operations
 */
export const useAgency = (): UseAgencyReturn => {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const agency = useSelector(selectAgency);
  const isLoading = useSelector(selectAgencyLoading);
  const error = useSelector(selectAgencyError);
  const lastFetched = useSelector(selectAgencyLastFetched);

  // Derived selectors
  const agencyInfo = useSelector(selectAgencyInfo);
  const addresses = useSelector(selectAgencyAddresses);
  const contact = useSelector(selectAgencyContact);
  const licensing = useSelector(selectAgencyLicensing);
  const services = useSelector(selectAgencyServices);
  const operations = useSelector(selectAgencyOperations);
  const insurance = useSelector(selectAgencyInsurance);
  const documents = useSelector(selectAgencyDocuments);
  const status = useSelector(selectAgencyStatus);

  /**
   * Fetch agency by ID
   */
  const fetchAgency = useCallback(
    async (agencyId: string) => {
      return await dispatch(fetchAgencyByIdAsync(agencyId)).unwrap();
    },
    [dispatch]
  );

  /**
   * Refresh current agency data
   */
  const refreshAgency = useCallback(async () => {
    if (agency?._id) {
      return await dispatch(fetchAgencyByIdAsync(agency._id)).unwrap();
    }
    return null;
  }, [dispatch, agency?._id]);

  /**
   * Clear agency data
   */
  const clearAgencyData = useCallback(() => {
    dispatch(clearAgency());
  }, [dispatch]);

  return {
    // State
    agency,
    isLoading,
    error,
    lastFetched,

    // Derived data
    agencyInfo,
    addresses,
    contact,
    licensing,
    services,
    operations,
    insurance,
    documents,
    status,

    // Actions
    fetchAgency,
    refreshAgency,
    clearAgencyData,
  };
};

export default useAgency;
